import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../../utils/interface";
import { uploadCkycDocs, verifyCkycId, readData, checkCkycStatus } from "../../../../service/order/orderService";
import { useSelector } from "react-redux";
import { KYC_FLOW_OPTIONS, KYC_UNAVAILABLE_MSG, KYC_FLOW_DISABLED_TEXT } from "./kycConstants";
import useCkycFlows from "./useCkycFlows";
import "./index.scss";

const KycModal = ({ isOpen, toggle, orderId, memberId, onSuccess, initialView }) => {
  const [view, setView] = useState("chooser"); // "chooser" | "doc-chooser" | "upload" | "verify"
  const [uploadDocType, setUploadDocType] = useState(""); // "AADHAAR" | "PAN"

  // Loading & Submission State
  const [loading, setLoading] = useState(false);
  const [submitProgress, setSubmitProgress] = useState("");

  // Flow 1 states (Upload)
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [panFile, setPanFile] = useState(null);

  // Flow 2 states (Verify ID)
  const [customerType, setCustomerType] = useState("I");
  const [idType, setIdType] = useState("PAN");
  const [idNum, setIdNum] = useState("");
  const [fullName, setFullName] = useState("");
  const [gender, setGender] = useState("M");
  const [dob, setDob] = useState("");
  const [mobile, setMobile] = useState("");
  const [proposalPanNum, setProposalPanNum] = useState("");
  const [proposalAadhaarNum, setProposalAadhaarNum] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusCheckMsg, setStatusCheckMsg] = useState("");

  const { data: flows, isFetching, isError, refetch } = useCkycFlows(isOpen);

  const detailsData = useSelector(
    (state) => state.orders.selectedOrder.detailsData
  );
  const FIELDS = useSelector((state) => state.buyNow.ProposalFieldsJSON);

  // Fetch details on orderId change
  useEffect(() => {
    if (isOpen && orderId) {
      readData(orderId, "ODR").catch((err) => {
        console.error("Failed to read order details in KycModal:", err);
      });
    }
  }, [isOpen, orderId]);

  // Set initial view based on initialView prop or CKYC status
  useEffect(() => {
    if (isOpen) {
      if (initialView) {
        setView(initialView);
      } else {
        const kycStatus = detailsData?.kyc_status;
        if (kycStatus === "Pending") {
          setView("status");
        } else {
          setView("chooser");
        }
      }
    }
  }, [isOpen, initialView, detailsData?.kyc_status]);

  const enabledOptions = KYC_FLOW_OPTIONS.filter((option) => flows?.[option.flag] === true);
  const isSingleFlow = enabledOptions.length === 1;

  // Option views wait for /ckyc/flows; with a single enabled flow the selector is skipped
  let activeView = view;
  if (view !== "status" && (isFetching || isError || !flows)) {
    activeView = isError && !isFetching ? "flows-error" : "flows-loading";
  } else if (view === "chooser" && isSingleFlow) {
    activeView = enabledOptions[0].view;
  }

  // 400 "... is disabled": the flow was switched off while the user was on the page
  const reloadFlowsIfDisabled = (error, errMsg) => {
    if (error?.response?.status === 400 && errMsg.includes(KYC_FLOW_DISABLED_TEXT)) {
      setView("chooser");
      refetch();
    }
  };

  // Pre-fill full name and contact from order details
  useEffect(() => {
    if (detailsData) {
      const name = detailsData.billing_name ||
        `${detailsData.billing_first_name || ""} ${detailsData.billing_last_name || ""}`.trim();
      setFullName(name);
      setMobile(detailsData.billing_contact || "");
    }
  }, [detailsData]);

  // Pre-fill Aadhaar, PAN & DOB details/files from proposal form data
  useEffect(() => {
    const prefillProposalFiles = async () => {
      if (!isOpen || !orderId) return;

      try {
        const organizationId = detailsData?.organizationid || detailsData?.ag_org_id || detailsData?.organization_id;
        let fd = new FormData();
        fd.append("orderId", orderId);
        fd.append("displayMode", "VIEW");
        if (organizationId) {
          fd.append("organizationId", organizationId);
        }

        const response = await api.post(`/agsp/order/getProposalFormData`, fd);
        const proposalData = response?.data;
        if (!proposalData) return;

        // 1. Try to find the keys dynamically from ProposalFieldsJSON in Redux
        let panKey = "";
        let aadhaarKey = "";
        let dobKey = "";

        if (FIELDS) {
          Object.keys(FIELDS).forEach((key) => {
            const field = FIELDS[key];
            if (field) {
              const label = (field["display-fieldlabel"] || "").toLowerCase().replace(/[^a-z0-9]/g, "");
              const dbLabel = field["database-label"];

              if (dbLabel) {
                if (label.includes("pan") && (label.includes("number") || label.includes("no") || label.includes("card")) && field["elementtype"] !== "file") {
                  panKey = dbLabel;
                }
                if ((label.includes("aadhaar") || label.includes("aadhar")) && (label.includes("number") || label.includes("no") || label.includes("card")) && field["elementtype"] !== "file") {
                  aadhaarKey = dbLabel;
                }
                if (label.includes("dateofbirth") || label === "dob") {
                  dobKey = dbLabel;
                }
              }
            }
          });
        }

        // 2. Fallback: Scan proposalData keys for matching patterns
        const keys = Object.keys(proposalData || {});
        if (!panKey) {
          panKey = keys.find(k => k.toLowerCase().includes("pan") && k.toLowerCase() !== "dn_pancard");
        }
        if (!aadhaarKey) {
          aadhaarKey = keys.find(k => (k.toLowerCase().includes("aadh") || k.toLowerCase().includes("aadhar")) && k.toLowerCase() !== "dn_aadhcard" && k.toLowerCase() !== "dn_aadhcard_back");
        }
        if (!dobKey) {
          dobKey = keys.find(k => k.toLowerCase().includes("dob") || k.toLowerCase().includes("birth"));
        }

        const fetchedPan = panKey ? proposalData[panKey] : "";
        const fetchedAadhaar = aadhaarKey ? proposalData[aadhaarKey] : "";
        const fetchedDob = dobKey ? proposalData[dobKey] : "";

        if (fetchedPan) {
          setProposalPanNum(fetchedPan);
          setIdNum(fetchedPan); // Set initial idNum as PAN since PAN is default idType
        }
        if (fetchedAadhaar) {
          setProposalAadhaarNum(fetchedAadhaar);
        }
        if (fetchedDob) {
          // Convert date format if needed: dd-MM-yyyy -> yyyy-MM-dd
          if (fetchedDob.includes("-")) {
            const parts = fetchedDob.split("-");
            if (parts[0].length === 2 && parts[2].length === 4) {
              setDob(`${parts[2]}-${parts[1]}-${parts[0]}`);
            } else {
              setDob(fetchedDob);
            }
          } else {
            setDob(fetchedDob);
          }
        }

        // Dynamically resolve PAN and Aadhaar upload file objects
        const panFileKey = keys.find(k => k.toLowerCase().includes("pan") && proposalData[k]?.media_url);
        const aadhaarFileKey = keys.find(k => (k.toLowerCase().includes("aadh") || k.toLowerCase().includes("aadhar")) && proposalData[k]?.media_url && !k.toLowerCase().includes("back"));

        const panFileObj = panFileKey ? proposalData[panFileKey] : proposalData.dn_pancard;
        const aadhaarFileObj = aadhaarFileKey ? proposalData[aadhaarFileKey] : proposalData.dn_aadhcard;

        const fetchFileFromUrl = async (url, filename) => {
          try {
            let targetUrl = url;
            if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
              const domain = api.defaults.baseURL || "";
              const cleanDomain = domain.replace(/\/api\/?$/, "");
              targetUrl = `${cleanDomain}${url.startsWith("/") ? "" : "/"}${url}`;
            }

            const res = await fetch(targetUrl);
            if (!res.ok) throw new Error("Fetch failed");
            const blob = await res.blob();
            if (blob.type && blob.type.includes("html")) {
              throw new Error("Downloaded file content resolves to HTML, likely a 404 SPA fallback redirect");
            }
            const mimeType = blob.type || "image/jpeg";
            return new File([blob], filename, { type: mimeType });
          } catch (e) {
            console.error("Error fetching file from URL:", e);
            return null;
          }
        };

        if (aadhaarFileObj?.media_url) {
          const file = await fetchFileFromUrl(aadhaarFileObj.media_url, aadhaarFileObj.file_name || "aadhaar_card.jpg");
          if (file) setAadhaarFile(file);
        }

        if (panFileObj?.media_url) {
          const file = await fetchFileFromUrl(panFileObj.media_url, panFileObj.file_name || "pan_card.jpg");
          if (file) setPanFile(file);
        }
      } catch (err) {
        console.error("Failed to prefill proposal files in KycModal:", err);
      }
    };

    prefillProposalFiles();
  }, [isOpen, orderId, detailsData, FIELDS]);

  const handleClose = () => {
    setView("chooser");
    setUploadDocType("");
    setAadhaarFile(null);
    setPanFile(null);
    setIdNum("");
    setDob("");
    setProposalPanNum("");
    setProposalAadhaarNum("");
    setStatusCheckMsg("");
    toggle();
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleDocUploadSubmit = async (e) => {
    e.preventDefault();
    if (uploadDocType === "AADHAAR" && !aadhaarFile) {
      toast.error("Please select Aadhaar document to upload.");
      return;
    }
    if (uploadDocType === "PAN" && !panFile) {
      toast.error("Please select PAN document to upload.");
      return;
    }

    setLoading(true);
    setSubmitProgress("Reading files...");

    try {
      let aadhaarBase64 = undefined;
      let panBase64 = undefined;

      if (uploadDocType === "AADHAAR" && aadhaarFile) {
        aadhaarBase64 = await readFileAsDataURL(aadhaarFile);
      }
      if (uploadDocType === "PAN" && panFile) {
        panBase64 = await readFileAsDataURL(panFile);
      }

      setSubmitProgress("Uploading files...");

      const payload = {
        member_id: parseInt(memberId, 10),
        order_id: parseInt(orderId, 10),
        ckyc_aadhaar: aadhaarBase64,
        ckyc_pan: panBase64,
      };

      const res = await uploadCkycDocs(payload);
      if (orderId) {
        await readData(orderId, "ODR").catch(() => { });
      }

      const fStatus =
        res?.final_status ??
        res?.member_ckyc?.final_status ??
        res?.provider_response?.response?.finalStatus;
      const kStatus = res?.kyc_status || res?.member_ckyc?.kyc_status;

      const isSuccess =
        kStatus === "Completed" ||
        res?.is_kyc ||
        res?.member_ckyc?.is_kyc ||
        fStatus === "1" ||
        fStatus === "3" ||
        fStatus === 1 ||
        fStatus === 3;

      const isFailed =
        kStatus === "Failed" ||
        fStatus === "0" ||
        fStatus === 0;

      if (isSuccess) {
        toast.success("Verification successful!");
        if (onSuccess) onSuccess(res);
        handleClose();
      } else if (isFailed) {
        toast.error(
          res?.ckyc_remarks ||
          res?.member_ckyc?.ckyc_remarks ||
          res?.provider_response?.response?.message ||
          "The upload was unsuccessful. Kindly re-upload the documents."
        );
      } else {
        toast.info("Documents uploaded successfully. Verification is under review.");
        if (onSuccess) onSuccess(res);
        handleClose();
      }
    } catch (error) {
      const errMsg = error?.response?.data?.error_message || "Upload failed.";
      toast.error(errMsg);
      reloadFlowsIfDisabled(error, errMsg);
    } finally {
      setLoading(false);
      setSubmitProgress("");
    }
  };

  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // The stashed ids only mean something when the customer is sent to the insurer and
  // comes back. Left behind otherwise, the payment summary reads them as a return from
  // the insurer and reports the outcome a second time.
  const clearCkycStash = () => {
    ["ckyc_member_id", "ckyc_order_id"].forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  };

  const handleVerifyIdSubmit = async (e) => {
    e.preventDefault();
    if (!idNum) {
      toast.error("Document ID Number is required");
      return;
    }
    if (!fullName) {
      toast.error("Full Name is required");
      return;
    }
    if (!dob) {
      toast.error("Date of Birth is required");
      return;
    }

    setLoading(true);
    try {
      sessionStorage.setItem("ckyc_member_id", memberId);
      sessionStorage.setItem("ckyc_order_id", orderId);

      const payload = {
        member_id: parseInt(memberId, 10),
        order_id: parseInt(orderId, 10),
        customer_type: customerType || "I",
        id_type: idType,
        id_num: idNum.toUpperCase().trim(),
        dob: dob ? formatDateToDDMMYYYY(dob) : undefined,
        full_name: fullName.trim(),
        gender: gender || undefined,
        mobile: mobile ? mobile.trim() : undefined,
      };

      const response = await verifyCkycId(payload);
      const resData = response; // verifyCkycId already returns response.data
      const redirectUrl = resData?.redirect_url; // FG portal url -> the form action
      const proposalId =
        resData?.member_ckyc?.proposal_id ||
        resData?.provider_response?.response?.proposalId;
      const ckycRef = resData?.member_ckyc?.ckyc_ref;

      const actualOrderId = orderId || resData?.member_ckyc?.order_id || detailsData?.entityid;
      const actualMemberId = memberId || resData?.member_ckyc?.member_id || detailsData?.billing_member_id || detailsData?.member_id;

      if (actualOrderId) {
        localStorage.setItem("ckyc_order_id", String(actualOrderId));
        sessionStorage.setItem("ckyc_order_id", String(actualOrderId));
      }
      if (actualMemberId) {
        localStorage.setItem("ckyc_member_id", String(actualMemberId));
        sessionStorage.setItem("ckyc_member_id", String(actualMemberId));
      }

      const apiBase = (api.defaults.baseURL || "").replace(/\/+$/, "");
      const insurerReturnUrl = ckycRef ? `${apiBase}/ckyc/return/${ckycRef}` : null;

      if (redirectUrl) {
        if (!insurerReturnUrl) {
          clearCkycStash();
          toast.error("Missing CKYC reference for return redirection. Please try again.");
          return;
        }

        const form = document.createElement("form");
        form.method = "POST";
        form.action = redirectUrl;

        const addHiddenField = (name, value) => {
          if (value !== undefined && value !== null && value !== "") {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value;
            form.appendChild(input);
          }
        };

        addHiddenField("VISoF_KYC_Req_No", proposalId);
        addHiddenField("IC_KYC_No", proposalId);
        addHiddenField("VISoF_Return_URL", insurerReturnUrl);

        document.body.appendChild(form);
        form.submit();
        return;
      }

      // no redirect - the outcome is final here and this modal reports it
      clearCkycStash();

      const fStatus =
        resData?.final_status ??
        resData?.member_ckyc?.final_status ??
        resData?.provider_response?.response?.finalStatus;
      const kStatus = resData?.kyc_status || resData?.member_ckyc?.kyc_status;

      const isSuccess =
        kStatus === "Completed" ||
        resData?.is_kyc ||
        resData?.member_ckyc?.is_kyc ||
        fStatus === "1" ||
        fStatus === "3" ||
        fStatus === 1 ||
        fStatus === 3;

      const isFailed =
        kStatus === "Failed" ||
        fStatus === "0" ||
        fStatus === 0;

      if (isSuccess) {
        toast.success("Verification successful!");
        if (orderId) {
          await readData(orderId, "ODR").catch(() => { });
        }
        if (onSuccess) onSuccess(response);
        handleClose();
      } else if (isFailed) {
        toast.error(
          resData?.ckyc_remarks ||
          resData?.member_ckyc?.ckyc_remarks ||
          resData?.provider_response?.response?.message ||
          "Verification failed."
        );
      } else {
        toast.info("Verification is under review.");
        if (orderId) {
          await readData(orderId, "ODR").catch(() => { });
        }
        if (onSuccess) onSuccess(response);
        handleClose();
      }
    } catch (error) {
      clearCkycStash();
      const errMsg = error?.response?.data?.error_message || "Verification failed.";
      toast.error(errMsg);
      reloadFlowsIfDisabled(error, errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleKycCheckStatus = async () => {
    setCheckingStatus(true);
    setStatusCheckMsg("Checking status...");
    try {
      const res = await checkCkycStatus(memberId, orderId);
      await readData(orderId, "ODR");

      const fStatus = res?.final_status ?? res?.provider_response?.response?.finalStatus;
      const isSuccess =
        res?.kyc_status === "Completed" ||
        res?.is_kyc ||
        fStatus === "1" ||
        fStatus === "3" ||
        fStatus === 1 ||
        fStatus === 3;

      const isFailed =
        res?.kyc_status === "Failed" ||
        fStatus === "0" ||
        fStatus === 0;

      if (isSuccess) {
        toast.success("KYC verified successfully!");
        if (onSuccess) onSuccess(res);
        handleClose();
      } else if (isFailed) {
        toast.error(`Verification Failed: ${res?.ckyc_remarks || "Please try again."}`);
        if (onSuccess) onSuccess(res);
        handleClose();
      } else {
        setStatusCheckMsg(`Your CKYC status is still ${res?.kyc_status || "Pending"}.`);
      }
    } catch (err) {
      const errMsg = err?.response?.data?.error_message || "Failed to check status";
      setStatusCheckMsg(`Error checking status: ${errMsg}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={handleClose} centered size="md" dialogClassName="kyc-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-semibold fs-5 text-dark">
          {activeView === "status" && "Central KYC (CKYC) Status"}
          {["chooser", "flows-loading", "flows-error"].includes(activeView) && "Central KYC (CKYC) Verification"}
          {activeView === "doc-chooser" && "Select Document to Upload"}
          {activeView === "upload" && `Upload ${uploadDocType === "AADHAAR" ? "Aadhaar Card" : "PAN Card"}`}
          {activeView === "verify" && "Verify with ID Details"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {loading && (
          <div className="kyc-blocking-loader text-center my-4 py-4">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>{submitProgress || "Processing Verification..."}</h5>
            <p className="text-muted small">Please do not close or refresh this window.</p>
          </div>
        )}

        {!loading && (
          <>
            {/* View S: Status main */}
            {activeView === "status" && (
              <div>
                <p className="kyc-description mb-4">
                  Your KYC verification is currently under review. Click check status below to query the latest updates.
                </p>
                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="kyc-status-info">
                    <span className="status-title">⏳ KYC Verification Pending</span>
                    <p className="status-desc">Your verification is currently being processed by the insurer registry.</p>
                  </div>
                  {statusCheckMsg && (
                    <div className="kyc-status-info p-3 text-center">
                      <span className="status-title">{statusCheckMsg}</span>
                    </div>
                  )}
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setView("chooser")}>
                    Re-upload Documents
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleKycCheckStatus} disabled={checkingStatus}>
                    {checkingStatus ? "Checking..." : "Check Status"}
                  </button>
                </div>
              </div>
            )}

            {/* Waiting for / failed to load the enabled KYC flows */}
            {activeView === "flows-loading" && (
              <div className="text-center my-4 py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            )}

            {activeView === "flows-error" && (
              <div className="text-center my-4">
                <p className="kyc-description mb-3">Unable to load KYC options. Please try again.</p>
                <button type="button" className="btn btn-primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            )}

            {/* View A: Chooser main */}
            {activeView === "chooser" && (
              enabledOptions.length ? (
                <div>
                  <p className="kyc-description mb-4">
                    Complete your KYC to finalize your order. Choose one of the two options below:
                  </p>
                  <div className="d-flex flex-column gap-3">
                    {enabledOptions.map((option) => (
                      <div
                        key={option.view}
                        className="kyc-option-card"
                        onClick={() => setView(option.view)}
                      >
                        <div className="kyc-option-text">
                          <h6 className="kyc-option-title">{option.title}</h6>
                          <p className="kyc-option-desc">{option.subtitle}</p>
                        </div>
                        <span className="kyc-option-arrow">&rarr;</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning mb-0">{KYC_UNAVAILABLE_MSG}</div>
              )
            )}

            {/* View B: Document Chooser */}
            {activeView === "doc-chooser" && (
              <div>
                <p className="kyc-description mb-4">
                  Select the document you wish to upload for verification:
                </p>
                <div className="d-flex flex-column gap-3">
                  <div
                    className="kyc-doc-chooser-card"
                    onClick={() => {
                      setUploadDocType("AADHAAR");
                      setView("upload");
                    }}
                  >
                    <div className="kyc-option-text">
                      <h6 className="kyc-option-title">Aadhaar Card</h6>
                      <p className="kyc-option-desc">Upload your Aadhaar Card (JPG, PNG, PDF)</p>
                    </div>
                    <span className="kyc-option-arrow">&rarr;</span>
                  </div>

                  <div
                    className="kyc-doc-chooser-card"
                    onClick={() => {
                      setUploadDocType("PAN");
                      setView("upload");
                    }}
                  >
                    <div className="kyc-option-text">
                      <h6 className="kyc-option-title">PAN Card</h6>
                      <p className="kyc-option-desc">Upload your PAN Card (JPG, PNG, PDF)</p>
                    </div>
                    <span className="kyc-option-arrow">&rarr;</span>
                  </div>
                </div>

                {!isSingleFlow && (
                  <div className="d-flex justify-content-end mt-4">
                    <button type="button" className="btn btn-secondary" onClick={() => setView("chooser")}>
                      Back
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* View C: Upload selected document */}
            {activeView === "upload" && (
              <Form onSubmit={handleDocUploadSubmit}>
                <p className="text-muted mb-4">
                  Please upload your {uploadDocType === "AADHAAR" ? "Aadhaar Card" : "PAN Card"} (JPEG, PNG, or PDF format).
                </p>

                <Row className="mb-4">
                  <Col xs={12}>
                    <Form.Group>
                      {uploadDocType === "AADHAAR" ? (
                        <>
                          <Form.Label className="fw-bold mb-2">Aadhaar Card File</Form.Label>
                          {aadhaarFile ? (
                            <div className="kyc-prefill-box">
                              <span className="prefill-file-info text-truncate">
                                📄 {aadhaarFile.name} <span className="prefill-tag">(Linked from Proposal)</span>
                              </span>
                              <button type="button" className="prefill-clear-btn" onClick={() => setAadhaarFile(null)}>Clear</button>
                            </div>
                          ) : (
                            <>
                              <Form.Control
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                onChange={(e) => setAadhaarFile(e.target.files[0])}
                              />
                              <small className="text-muted">JPEG, PNG, or PDF</small>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <Form.Label className="fw-bold mb-2">PAN Card File</Form.Label>
                          {panFile ? (
                            <div className="kyc-prefill-box">
                              <span className="prefill-file-info text-truncate">
                                📄 {panFile.name} <span className="prefill-tag">(Linked from Proposal)</span>
                              </span>
                              <button type="button" className="prefill-clear-btn" onClick={() => setPanFile(null)}>Clear</button>
                            </div>
                          ) : (
                            <>
                              <Form.Control
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                onChange={(e) => setPanFile(e.target.files[0])}
                              />
                              <small className="text-muted">JPEG, PNG, or PDF</small>
                            </>
                          )}
                        </>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setView("doc-chooser")}>
                    Back
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploadDocType === "AADHAAR" ? !aadhaarFile : !panFile}>
                    Submit Documents
                  </button>
                </div>
              </Form>
            )}

            {/* View D: Verify ID */}
            {activeView === "verify" && (
              <Form onSubmit={handleVerifyIdSubmit}>
                <Row className="mb-3">
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">ID Document Type</Form.Label>
                      <Form.Select
                        value={idType}
                        onChange={(e) => {
                          const val = e.target.value;
                          setIdType(val);
                          setIdNum(val === "PAN" ? proposalPanNum : proposalAadhaarNum);
                        }}
                      >
                        <option value="PAN">PAN</option>
                        <option value="AADHAAR">AADHAAR</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">Full Name (as per ID)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">{idType === "PAN" ? "PAN Number" : "Aadhaar Number"}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={`Enter ${idType === "PAN" ? "PAN Number" : "Aadhaar Number"}`}
                        value={idNum}
                        onChange={(e) => setIdNum(e.target.value.toUpperCase())}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">Date of Birth</Form.Label>
                      <Form.Control
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  {!isSingleFlow && (
                    <button type="button" className="btn btn-secondary" onClick={() => setView("chooser")}>
                      Back
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary">
                    Verify & Redirect
                  </button>
                </div>
              </Form>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default KycModal;
